/**
 * checkOut write op — PATCHes /api/projects/calllog/{calllogid}/ to
 * stamp the rep's check-out time (edcalltime), manager notes, and
 * (later) signature. Depends on the parent visit having synced so the
 * server-assigned calllogid can be substituted into the endpoint.
 *
 * Reconcile is a no-op: the server's PATCH response is the rep's own
 * fields echoed back, nothing to stamp onto the local row.
 *
 * EMITTED BY: StoreDetail.tsx:2458 (store_checked_out), which fires AFTER the
 * checkout completes so the event carries the outcome. Deliberately not emitted
 * here as well — two events for one checkout would show a DM the rep leaving
 * twice, and reconstruct_active_day would replay it twice. If this op ever
 * gains a second caller, move the emit here and delete the screen's (#3530).
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import {
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveCallLogUrl,
} from '../httpHelpers';

export interface CheckOutInput {
  visitLocalId: string;
  /**
   * Rep's check-out time. The single-task call site sends HH:MM (legacy
   * format) and the multi-task drain sends YYYY-MM-DDTHH:MM:SS — the
   * server PATCH accepts both. May be omitted entirely for full-manual
   * entries whose edcalltime was already stamped at creation; in that
   * case only the signature / team fields are PATCHed.
   */
  edcalltime?: string;
  notes?: string;
  managerName?: string;
  signatureDataUrl?: string | null;
  noSignatureReason?: string | null;
  /** Team-visit flags, mirrored from the bulk submit. */
  teamproject?: boolean;
  teamleader?: boolean;
  driveodoend?: number;
  eddrivetime?: string;
}

export interface CheckOutResponse {
  calllogid: number;
  edcalltime: string;
}

export const checkOutOp: WriteOp<CheckOutInput, CheckOutInput, CheckOutResponse> = {
  kind: 'checkOut',

  async applyLocal(input) {
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === input.visitLocalId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, {
      ...match,
      status: 'completed',
      endTime: input.edcalltime ?? match.endTime,
      checkOutTime: input.edcalltime ?? match.checkOutTime,
      notes: input.notes ?? match.notes,
      managerName: input.managerName ?? match.managerName,
      signatureDataUrl: input.signatureDataUrl ?? match.signatureDataUrl,
      noSignatureReason: input.noSignatureReason ?? match.noSignatureReason,
    });
  },

  serialize(input, localId) {
    return {
      action: 'checkOut',
      endpoint: '/api/projects/calllog/{calllogid}/',
      method: 'PATCH',
      payload: input,
      priority: 20,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as CheckOutInput;
    const urlOrShortCircuit = await resolveCallLogUrl(row, input.visitLocalId);
    if (typeof urlOrShortCircuit !== 'string') return urlOrShortCircuit;
    const url = urlOrShortCircuit;

    const body: Record<string, unknown> = {};
    if (input.edcalltime !== undefined) body.edcalltime = input.edcalltime;
    if (input.notes !== undefined) body.notes = input.notes;
    // Mirror the bulk submit's wire field names so the single-task PATCH
    // carries the rep's signature + manager + team flags (#1987).
    if (input.managerName !== undefined) body.manager_name = input.managerName;
    if (input.signatureDataUrl !== undefined) body.signature = input.signatureDataUrl;
    if (input.noSignatureReason !== undefined) body.no_signature_reason = input.noSignatureReason;
    if (input.teamproject !== undefined) body.teamproject = input.teamproject;
    if (input.teamleader !== undefined) body.teamleader = input.teamleader;
    if (input.driveodoend !== undefined) body.driveodoend = input.driveodoend;
    if (input.eddrivetime !== undefined) body.eddrivetime = input.eddrivetime;

    let response: Response;
    try {
      response = await fetch(url, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify(body),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    const data = await response.json().catch(() => ({}));
    const callLog = data.data || data;
    return {
      status: 'success',
      serverId: typeof callLog?.calllogid === 'number' ? callLog.calllogid : undefined,
      retriable: false,
    };
  },

  async reconcile() {
    // Server response carries no new IDs; local row was already updated in
    // applyLocal. Nothing to stamp.
  },

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type CheckOutOp = typeof checkOutOp;
